import React from 'react';
import TableRenderer from './TableRenderer';

const PerformanceDashboardRenderer = ({ data }) => {
  if (!data || !data.dashboard) return null;

  const { dashboard } = data;

  return (
    <div style={{ 
      background: 'white', 
      padding: '20px', 
      borderRadius: '12px', 
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      margin: '16px 0'
    }}>
      {/* Dashboard Title */}
      {dashboard.title && (
        <div style={{ 
          marginBottom: '24px', 
          textAlign: 'center',
          borderBottom: '2px solid #1565c0',
          paddingBottom: '16px'
        }}>
          <h2 style={{ 
            margin: '0', 
            color: '#1565c0', 
            fontSize: '24px',
            fontWeight: 'bold'
          }}>
            {dashboard.title}
          </h2>
        </div>
      )}

      {/* Executive Summary */}
      {dashboard.executiveSummary && (
        <div style={{ 
          marginBottom: '24px', 
          padding: '16px', 
          background: '#f8f9fa', 
          borderRadius: '8px',
          borderLeft: '4px solid #1565c0'
        }}>
          <h3 style={{ 
            margin: '0 0 12px 0', 
            color: '#1565c0', 
            fontSize: '18px' 
          }}>
            📊 Executive Summary
          </h3>
          <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
            <div><strong>Status:</strong> {dashboard.executiveSummary.status}</div>
            <div><strong>Progress:</strong> {dashboard.executiveSummary.progress}</div>
            <div><strong>Performance Gap:</strong> {dashboard.executiveSummary.gap}</div>
            <div><strong>Projected Completion:</strong> {dashboard.executiveSummary.projection}</div>
            {dashboard.executiveSummary.velocity && (
              <div><strong>Velocity:</strong> {dashboard.executiveSummary.velocity}</div>
            )}
            {dashboard.executiveSummary.consistency && (
              <div><strong>Consistency:</strong> {dashboard.executiveSummary.consistency}</div>
            )}
            {dashboard.executiveSummary.momentum && (
              <div><strong>Momentum:</strong> {dashboard.executiveSummary.momentum}</div>
            )}
          </div>
        </div>
      )}

      {/* Performance Metrics Table */}
      {dashboard.performanceMetrics && (
        <div style={{ marginBottom: '24px' }}>
          <TableRenderer data={dashboard.performanceMetrics} />
        </div>
      )}

      {/* Employee Performance Table */}
      {dashboard.employeePerformance && (
        <div style={{ marginBottom: '24px' }}>
          <TableRenderer data={dashboard.employeePerformance} />
        </div>
      )}

      {/* Cascade Analysis Table */}
      {dashboard.cascadeAnalysis && (
        <div style={{ marginBottom: '24px' }}>
          <TableRenderer data={dashboard.cascadeAnalysis} />
        </div>
      )}

      {/* Split Analysis Table */}
      {dashboard.splitAnalysis && (
        <div style={{ marginBottom: '24px' }}>
          <TableRenderer data={dashboard.splitAnalysis} />
        </div>
      )}

      {/* Progress Updates Table */}
      {dashboard.progressUpdates && (
        <div style={{ marginBottom: '24px' }}>
          <TableRenderer data={dashboard.progressUpdates} />
        </div>
      )}

      {/* Cascade Updates Table */}
      {dashboard.cascadeUpdates && (
        <div style={{ marginBottom: '24px' }}>
          <TableRenderer data={dashboard.cascadeUpdates} />
        </div>
      )}

      {/* Split Updates Table */}
      {dashboard.splitUpdates && (
        <div style={{ marginBottom: '24px' }}>
          <TableRenderer data={dashboard.splitUpdates} />
        </div>
      )}

      {/* Risk Assessment Matrix */}
      {dashboard.riskMatrix && (
        <div style={{ marginBottom: '24px' }}>
          <TableRenderer data={dashboard.riskMatrix} />
        </div>
      )}

      {/* Legacy Support - Cascade Plans Table */}
      {dashboard.cascadePlans && (
        <div style={{ marginBottom: '24px' }}>
          <TableRenderer data={dashboard.cascadePlans} />
        </div>
      )}

      {/* Legacy Support - Split Plans Table */}
      {dashboard.splitPlans && (
        <div style={{ marginBottom: '24px' }}>
          <TableRenderer data={dashboard.splitPlans} />
        </div>
      )}

      {/* Performance Optimization Table */}
      {dashboard.optimizationMatrix && (
        <div style={{ marginBottom: '24px' }}>
          <TableRenderer data={dashboard.optimizationMatrix} />
        </div>
      )}

      {/* Comprehensive Trend Analysis Section */}
      {dashboard.trendAnalysis && (
        <div style={{ 
          marginBottom: '24px', 
          padding: '16px', 
          background: '#f0f7ff', 
          borderRadius: '8px',
          borderLeft: '4px solid #2196f3'
        }}>
          <h3 style={{ 
            margin: '0 0 12px 0', 
            color: '#1976d2', 
            fontSize: '16px' 
          }}>
            📊 Comprehensive Trend Analysis
          </h3>
          
          {dashboard.trendAnalysis.summary && (
            <div style={{ marginBottom: '16px', padding: '12px', background: '#e3f2fd', borderRadius: '6px' }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#1976d2' }}>
                Data Summary:
              </h4>
              <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                <div><strong>Total Updates:</strong> {dashboard.trendAnalysis.summary.totalUpdates}</div>
                <div><strong>Cascade Updates:</strong> {dashboard.trendAnalysis.summary.cascadeUpdates}</div>
                <div><strong>Split Updates:</strong> {dashboard.trendAnalysis.summary.splitUpdates}</div>
                <div><strong>Update Frequency:</strong> {dashboard.trendAnalysis.summary.updateFrequency}</div>
                <div><strong>Data Quality:</strong> {dashboard.trendAnalysis.summary.dataAvailability}</div>
              </div>
            </div>
          )}

          {dashboard.trendAnalysis.insights && (
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#1976d2' }}>
                Key Insights:
              </h4>
              <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px' }}>
                {dashboard.trendAnalysis.insights.map((insight, index) => (
                  <li key={index} style={{ marginBottom: '4px' }}>{insight}</li>
                ))}
              </ul>
            </div>
          )}

          {dashboard.trendAnalysis.recommendations && (
            <div>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#f57c00' }}>
                Trend-Based Recommendations:
              </h4>
              <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px' }}>
                {dashboard.trendAnalysis.recommendations.map((rec, index) => (
                  <li key={index} style={{ marginBottom: '4px' }}>{rec}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Progress Analysis */}
      {dashboard.progressAnalysis && (
        <div style={{ 
          marginBottom: '24px', 
          padding: '16px', 
          background: '#f0f7ff', 
          borderRadius: '8px',
          borderLeft: '4px solid #2196f3'
        }}>
          <h3 style={{ 
            margin: '0 0 12px 0', 
            color: '#1976d2', 
            fontSize: '16px' 
          }}>
            📈 Progress Analysis
          </h3>
          <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
            <div><strong>Recent Trend:</strong> {dashboard.progressAnalysis.trend}</div>
            <div><strong>Update Frequency:</strong> {dashboard.progressAnalysis.frequency}</div>
            <div><strong>Consistency:</strong> {dashboard.progressAnalysis.consistency}</div>
            {dashboard.progressAnalysis.recentUpdates && (
              <div style={{ marginTop: '12px' }}>
                <strong>Recent Updates:</strong>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  {dashboard.progressAnalysis.recentUpdates.map((update, index) => (
                    <li key={index} style={{ marginBottom: '4px' }}>{update}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Strategic Recommendations */}
      {dashboard.recommendations && (
        <div style={{ 
          marginBottom: '24px', 
          padding: '16px', 
          background: '#fff3e0', 
          borderRadius: '8px',
          borderLeft: '4px solid #ff9800'
        }}>
          <h3 style={{ 
            margin: '0 0 12px 0', 
            color: '#f57c00', 
            fontSize: '16px' 
          }}>
            🎯 Strategic Recommendations
          </h3>
          
          {dashboard.recommendations.immediate && (
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#d32f2f' }}>
                Immediate Actions (Next 7 days):
              </h4>
              <ul style={{ margin: '0', paddingLeft: '20px' }}>
                {dashboard.recommendations.immediate.map((action, index) => (
                  <li key={index} style={{ marginBottom: '4px', fontSize: '13px' }}>{action}</li>
                ))}
              </ul>
            </div>
          )}

          {dashboard.recommendations.strategic && (
            <div>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#1976d2' }}>
                Strategic Initiatives (Next 30 days):
              </h4>
              <ul style={{ margin: '0', paddingLeft: '20px' }}>
                {dashboard.recommendations.strategic.map((action, index) => (
                  <li key={index} style={{ marginBottom: '4px', fontSize: '13px' }}>{action}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Next Steps */}
      {dashboard.nextSteps && (
        <div style={{ 
          padding: '16px', 
          background: '#e8f5e8', 
          borderRadius: '8px',
          borderLeft: '4px solid #4caf50'
        }}>
          <h3 style={{ 
            margin: '0 0 12px 0', 
            color: '#388e3c', 
            fontSize: '16px' 
          }}>
            📋 Next Steps & Follow-up
          </h3>
          <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
            {dashboard.nextSteps.priorities && (
              <div style={{ marginBottom: '12px' }}>
                <strong>Immediate Priorities:</strong>
                <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  {dashboard.nextSteps.priorities.map((priority, index) => (
                    <li key={index} style={{ marginBottom: '4px' }}>{priority}</li>
                  ))}
                </ol>
              </div>
            )}
            
            {dashboard.nextSteps.followUpQuestions && (
              <div>
                <strong>Recommended Follow-up Questions:</strong>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  {dashboard.nextSteps.followUpQuestions.map((question, index) => (
                    <li key={index} style={{ marginBottom: '4px' }}>{question}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Analysis Footer */}
      <div style={{ 
        marginTop: '24px', 
        padding: '12px', 
        background: '#f5f5f5', 
        borderRadius: '6px',
        fontSize: '12px',
        color: '#666',
        textAlign: 'center'
      }}>
        Analysis generated on {new Date().toLocaleDateString()} | 
        Next review recommended: {new Date(Date.now() + 7*24*60*60*1000).toLocaleDateString()}
      </div>
    </div>
  );
};

export default PerformanceDashboardRenderer;